import type { Class } from "type-fest";

export class AppEvent<T extends { type: string } = { type: string }> extends CustomEvent<T> {
  constructor(detail: T) {
    super("app-event", { detail });
  }
}

export function subscribeToAppEvents(listener: (event: AppEvent) => void) {
  const handleEvent: EventListener = (event) => {
    if (event instanceof AppEvent) {
      listener(event);
    }
  };

  window.addEventListener("app-event", handleEvent);

  return () => {
    window.removeEventListener("app-event", handleEvent);
  };
}

const debounceMap = new Map<string, NodeJS.Timeout>();

export function emitAppEvent<E extends Class<AppEvent>>(
  event: E,
  ...args: ConstructorParameters<E>[0] extends undefined ? [] : [ConstructorParameters<E>[0]]
) {
  const eventInstance = new event(args[0]);
  const eventType = eventInstance.detail.type;

  if (debounceMap.has(eventType)) {
    clearTimeout(debounceMap.get(eventType));
  }

  // Mitigates double event dispatching in useEffects
  const timeoutId = setTimeout(() => {
    window.dispatchEvent(eventInstance);
    debounceMap.delete(eventType);
  }, 100);

  debounceMap.set(eventType, timeoutId);
}
